import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getList,
} from "@surya-digital/leo-ts-types";
import { Code, CodeDTO } from "../types/code";
import { RespondentColumnName, RespondentColumnNameDTO } from "../types/respondentColumnName";


export namespace SurveyRuleConditionEnums {
  export namespace KeyType {
    export interface QuestionDTO {
      case: string;
      conditionKey: CodeDTO;
      conditionValue: CodeDTO[];
    }
    
    export class Question {
      readonly isKeyTypeQuestion: boolean = true;
    
      readonly conditionKey: Code;
      readonly conditionValue: Code[];
    
      static readonly constraints = {
        "conditionValue": {
          "minSize": 1,
        }
      }
    
      constructor(
      conditionKey: Code, 
      conditionValue: Code[]
      ) {
        this.conditionKey = conditionKey;
        Question.validateConditionValue(conditionValue);
        this.conditionValue = conditionValue;
      }
    
      static fromDTO(dto: QuestionDTO): Question {
        const conditionKey = Code.fromDTO(getObject(dto, "conditionKey", InvalidSurveyRuleConditionKeyTypeQuestionConditionKeyError));
        const conditionValue = getList<CodeDTO, Code>(
          dto,
          "conditionValue",
          Code.fromDTO,
          InvalidSurveyRuleConditionKeyTypeQuestionConditionValueError
        );
        return new Question(
          conditionKey,
          conditionValue
        );
      }
    
      toDTO(): QuestionDTO {
        const dto: QuestionDTO = {
          case: "QUESTION",
            conditionKey: this.conditionKey.toDTO(),
            conditionValue: this.conditionValue.map((e: Code) => { return e.toDTO() })
        };
        return dto;
      }
      private static validateConditionValue = (conditionValue: Code[]): void => {
        if(conditionValue.length < 1) {
          throw new InvalidSurveyRuleConditionKeyTypeQuestionConditionValueMinSizeError(
            1,
            `Attribute conditionValue is too short. Size is ${conditionValue.length}. Min size is 1.`
          );
        }
      }
      
    }
    
    Question.prototype.toString = function toString(): string {
      return `KeyType.QUESTION(conditionKey='${this.conditionKey}',conditionValue='${this.conditionValue}')`;
    };
    
    export interface RespondentDTO {
      case: string;
      conditionKey: RespondentColumnNameDTO;
      conditionValue: string[];
    }
    
    export class Respondent {
      readonly isKeyTypeRespondent: boolean = true;
    
      readonly conditionKey: RespondentColumnName;
      readonly conditionValue: string[];
    
      static readonly constraints = {
        "conditionValue": {
          "minSize": 1,
        }
      }
    
      constructor(
      conditionKey: RespondentColumnName, 
      conditionValue: string[]
      ) {
        this.conditionKey = conditionKey;
        Respondent.validateConditionValue(conditionValue);
        this.conditionValue = conditionValue;
      }
    
      static fromDTO(dto: RespondentDTO): Respondent {
        const conditionKey = RespondentColumnName.fromDTO(getObject(dto, "conditionKey", InvalidSurveyRuleConditionKeyTypeQuestionConditionKeyError));
        const conditionValue = getList<string, string>(
          dto,
          "conditionValue",
          (e: string) => e,
          InvalidSurveyRuleConditionKeyTypeQuestionConditionValueError
        );
        return new Respondent(
          conditionKey,
          conditionValue
        );
      }
    
      toDTO(): RespondentDTO {
        const dto: RespondentDTO = {
          case: "RESPONDENT",
            conditionKey: this.conditionKey.toDTO(),
            conditionValue: this.conditionValue
        };
        return dto;
      }
      private static validateConditionValue = (conditionValue: string[]): void => {
        if(conditionValue.length < 1) {
          throw new InvalidSurveyRuleConditionKeyTypeQuestionConditionValueMinSizeError(
            1,
            `Attribute conditionValue is too short. Size is ${conditionValue.length}. Min size is 1.`
          );
        }
      }
      
    }
    
    Respondent.prototype.toString = function toString(): string {
      return `KeyType.RESPONDENT(conditionKey='${this.conditionKey}',conditionValue='${this.conditionValue}')`;
    };
    
  
    export type KeyType = Question | Respondent;
  
    export type KeyTypeDTO = QuestionDTO | RespondentDTO;
  
    export const fromDTO = (dto: KeyTypeDTO): KeyType => {
      switch (dto.case) {
        case "QUESTION":
            return Question.fromDTO(dto as QuestionDTO);
        case "RESPONDENT":
            return Respondent.fromDTO(dto as RespondentDTO);
        default:
          throw new InvalidSurveyRuleConditionKeyTypeError(
              `Case ${dto.case} is not valid case of KeyType.`
          );
      }
    };
  }
  
  /* eslint-disable @typescript-eslint/no-shadow */
  export namespace Connector {
    export enum Connector {
      AND = "AND",
      NONE = "NONE",
    }
  
    export interface ConnectorDTO {
      case: string;
    }
  
    export const fromDTO = (dto: ConnectorDTO): Connector => {
      if (dto === undefined || dto === null) {
        throw new InvalidSurveyRuleConditionConnectorError(
          `Invalid DTO ${dto} provided for Connector.Connector`
        );
      }
      switch (dto.case) {
        case "AND":
          return Connector.AND;
        case "NONE":
          return Connector.NONE;
        default:
          throw new InvalidSurveyRuleConditionConnectorError(
            `Case ${dto.case} is not valid case of Connector.Connector`
          );
      }
    };
  
    export const toDTO = (connector: Connector): ConnectorDTO => {
      const enumDTO = {
        case: Connector[connector],
      };
      return enumDTO;
    };
  }
  
}
export interface SurveyRuleConditionDTO {
  conditionId: string;
  keyType: SurveyRuleConditionEnums.KeyType.KeyTypeDTO;
  connector: SurveyRuleConditionEnums.Connector.ConnectorDTO;
}

export class SurveyRuleCondition {
  readonly isSurveyRuleConditionSurveyRuleCondition: boolean = true;

  readonly conditionId: LeoUUID;
  readonly keyType: SurveyRuleConditionEnums.KeyType.KeyType;
  readonly connector: SurveyRuleConditionEnums.Connector.Connector;


  constructor(
  conditionId: LeoUUID, 
  keyType: SurveyRuleConditionEnums.KeyType.KeyType, 
  connector: SurveyRuleConditionEnums.Connector.Connector
  ) {
    this.conditionId = conditionId;
    this.keyType = keyType;
    this.connector = connector;
  }

  static fromDTO(dto: SurveyRuleConditionDTO): SurveyRuleCondition {
    const conditionId = getLeoUuid(dto, "conditionId", InvalidSurveyRuleConditionConditionIdError);
    const keyType = SurveyRuleConditionEnums.KeyType.fromDTO(getObject(dto, "keyType", InvalidSurveyRuleConditionKeyTypeError));
    const connector = SurveyRuleConditionEnums.Connector.fromDTO(getObject(dto, "connector", InvalidSurveyRuleConditionConnectorError));
    return new SurveyRuleCondition(
      conditionId,
      keyType,
      connector
    );
  }

  toDTO(): SurveyRuleConditionDTO {
    const dto: SurveyRuleConditionDTO = {
        conditionId: this.conditionId.toDTO(),
        keyType: this.keyType.toDTO(),
        connector: SurveyRuleConditionEnums.Connector.toDTO(this.connector)
    };
    return dto;
  }
  }

SurveyRuleCondition.prototype.toString = function toString(): string {
  return `SurveyRuleCondition(conditionId='${this.conditionId}',keyType='${this.keyType}',connector='${this.connector}')`;
};

export class InvalidSurveyRuleConditionError extends Error {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleConditionError";
  }
}
export class InvalidSurveyRuleConditionConditionIdError extends InvalidSurveyRuleConditionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleConditionConditionIdError";
  }
}

export class InvalidSurveyRuleConditionKeyTypeError extends InvalidSurveyRuleConditionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleConditionKeyTypeError";
  }
}

export class InvalidSurveyRuleConditionKeyTypeQuestionError extends InvalidSurveyRuleConditionKeyTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleConditionKeyTypeQuestionError";
  }
}

export class InvalidSurveyRuleConditionKeyTypeQuestionConditionKeyError extends InvalidSurveyRuleConditionKeyTypeQuestionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleConditionKeyTypeQuestionConditionKeyError";
  }
}

export class InvalidSurveyRuleConditionKeyTypeQuestionConditionValueError extends InvalidSurveyRuleConditionKeyTypeQuestionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleConditionKeyTypeQuestionConditionValueError";
  }
}

export class InvalidSurveyRuleConditionKeyTypeQuestionConditionValueMinSizeError extends InvalidSurveyRuleConditionKeyTypeQuestionConditionValueError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleConditionKeyTypeQuestionConditionValueMinSizeError";
  }
}

export class InvalidSurveyRuleConditionKeyTypeRespondentError extends InvalidSurveyRuleConditionKeyTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleConditionKeyTypeRespondentError";
  }
}

export class InvalidSurveyRuleConditionKeyTypeRespondentConditionKeyError extends InvalidSurveyRuleConditionKeyTypeRespondentError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleConditionKeyTypeRespondentConditionKeyError";
  }
}

export class InvalidSurveyRuleConditionKeyTypeRespondentConditionValueError extends InvalidSurveyRuleConditionKeyTypeRespondentError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleConditionKeyTypeRespondentConditionValueError";
  }
}

export class InvalidSurveyRuleConditionKeyTypeRespondentConditionValueMinSizeError extends InvalidSurveyRuleConditionKeyTypeRespondentConditionValueError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleConditionKeyTypeRespondentConditionValueMinSizeError";
  }
}

export class InvalidSurveyRuleConditionConnectorError extends InvalidSurveyRuleConditionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleConditionConnectorError";
  }
}

export class InvalidSurveyRuleConditionConnectorAndError extends InvalidSurveyRuleConditionConnectorError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleConditionConnectorAndError";
  }
}

export class InvalidSurveyRuleConditionConnectorNoneError extends InvalidSurveyRuleConditionConnectorError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidSurveyRuleConditionConnectorNoneError";
  }
}
